
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { PRODUCT_STOCK_STATUSES, PRODUCT_STATUSES } from 'constants/statuses'

import type { Product } from 'types'

// Extend Product type to include the aggregate counts the list API returns.
interface ProductWithCertificatesCount extends Product {
  certificates_count: number
  owners_count: number
}

// Extract a seeded reference id from the Table's `?filters=field:op:value` param
// (e.g. `owner:isAnyOf:<id>`) — the same format the shared Table parser consumes
// (Table.tsx splits `?filters=` on `&` then `:`). Powers the owner prefilter that
// OwnersPage's products-count cell links to
// (`/products?filters=owner:isAnyOf:<id>`). Mirrors CertificatesPage/OwnersPage's
// parseReferenceFilter helper.
const parseReferenceFilter = (filterParam: string | null, field: string): string | undefined => {
  if (!filterParam) return undefined
  for (const clause of filterParam.split('&')) {
    const [f, , value] = clause.split(':')
    if (f === field) return value || undefined
  }
  return undefined
}

import { useCanAccess, useCurrentCompany, useReferrerState } from 'hooks'
import { useProducts, useProductMutations, useShopifyProducts, useSyncProductFromShopify } from 'hooks/api'
import { useSnackbar } from 'contexts'
import { createApiCall } from 'services'
import { getErrorMessage } from 'utils'
import { PlusIcon, TrashIcon, PencilIcon, InfoIcon, BadgePlusIcon, ShoppingBagIcon, RefreshCw, ArchiveIcon, ArchiveRestoreIcon, UserCheckIcon } from 'lucide-react'
import { ROUTES } from 'routes'

import { Stack, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams, GridRowSelectionModel} from '@mui/x-data-grid-pro';

import { Button, ToggleField } from '@repo/ui'
import {
  ActionDropdown,
  Header,
  AddModal,
  DeleteProductModal,
  CreateCertificatesModal,
  RecordSaleModal,
  BulkDeleteProductsModal,
  Table,
  EmptyState,
  CategoryCell,
  ProductCell,
  ProductPreview,
  StatusChipCell,
  LinkCell,
  AggregateCountCell,
  UserCell,
  IntroDataBanner,
} from 'components'
import { headerWithTerm } from 'components/display/Table'
// import { Button } from '@repo/ui'

type Filters = {
  limit: number
  offset: number
  search: string | null
}

const DEFAULT_FILTERS: Filters = {
  limit: 10,
  offset: 0,
  search: null,
}

export const ProductsPage = () => {
  const navigate = useNavigate()
  // Hands "← Back to Products" (plus this list's filters/sort) to every detail page linked from here.
  const referrer = useReferrerState()
  const [searchParams] = useSearchParams()
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_FILTERS.limit })

  // Use useEffect to stabilize the initial pagination model
  useEffect(() => {
    // Component mounted successfully
  }, []) // Empty dependency array ensures this only runs once on mount

  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openCreateCertificatesModal, setOpenCreateCertificatesModal] = useState(false)
  // VRG-455 — assign-to-buyer, opened from a row's kebab with that row seeded.
  const [openRecordSaleModal, setOpenRecordSaleModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<ProductWithCertificatesCount | null>(null)
  // VRG-361 — arrive with the Add modal already open.
  //
  // Set by the Demos console's "Add products" action, which enters the demo's
  // space and lands here. The menu item promises adding products, so stopping at
  // the list would be one click short of what it said.
  //
  // Seeded from router state rather than a query param, and CLEARED on mount
  // below, so a browser Back does not re-open it and the URL stays shareable.
  const location = useLocation()
  const [openAddModal, setOpenAddModal] = useState(
    Boolean((location.state as {openAddModal?: boolean} | null)?.openAddModal),
  )

  useEffect(() => {
    if ((location.state as {openAddModal?: boolean} | null)?.openAddModal) {
      // replace, so the flag does not survive in history.
      navigate(location.pathname + location.search, {replace: true, state: null})
    }
    // Mount only — re-running on every location change would fight the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [openBulkDeleteModal, setOpenBulkDeleteModal] = useState(false)
  const [selectedProducts, setSelectedProducts] = useState<ProductWithCertificatesCount[]>([])
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([])
  const [syncingProductId, setSyncingProductId] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  // Staged value for the Filter drawer; committed to showArchived on Apply.
  const [showArchivedDraft, setShowArchivedDraft] = useState(false)

  const { open: notification } = useSnackbar()

  // Owners → Products reference filter: when navigated to as
  // `/products?filters=owner:isAnyOf:<id>` (e.g. from an owner's products-count
  // cell on OwnersPage), seed the server-side `ownerId` from the URL.
  const ownerId = parseReferenceFilter(searchParams.get('filters'), 'owner')

  // Memoize the products query parameters to prevent unnecessary re-fetches
  const productsQueryParams = useMemo(() => ({
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    includeArchived: showArchived,
    ownerId,
  }), [paginationModel.pageSize, paginationModel.page, showArchived, ownerId])

  const { data, isLoading, refetch } = useProducts(productsQueryParams)

  // VRG-227 — Download used to export only the loaded page (10-14 style shortfall).
  // Re-issues the SAME query `useProducts` above already drives (filters + search baked
  // into `productsQueryParams`), just with a different limit/offset, so Table can walk
  // every matching row for export. Called directly via `createApiCall` rather than through
  // `useProducts`/react-query — a paging loop that shares that hook's query key would
  // stomp the on-screen page's cached data with whatever export batch last landed.
  const fetchExportPage = useCallback(async ({ limit, offset }: { limit: number; offset: number }) => {
    const response = await createApiCall({
      method: 'GET',
      url: '/products/admin/company',
      params: { ...productsQueryParams, limit, offset },
    })
    const body = response.data as { data: ProductWithCertificatesCount[]; total: number }
    return { rows: body?.data ?? [], total: body?.total ?? 0 }
  }, [productsQueryParams])
  const { syncProduct, isLoading: isSyncingProduct } = useSyncProductFromShopify({
    onSuccess: () => {
      setSyncingProductId(null)
      refetch()
    },
    onError: () => {
      setSyncingProductId(null)
    }
  })

  const products = (data?.data as ProductWithCertificatesCount[]) || []
  const total = data?.total
  
  // Use ref to remember the last valid total to prevent undefined flickers
  const lastValidTotalRef = useRef<number | undefined>(undefined)
  if (total !== undefined) {
    lastValidTotalRef.current = total
  }
  const stableTotal = total ?? lastValidTotalRef.current


  const { company } = useCurrentCompany()
  const { data: shopifyProductsData } = useShopifyProducts({
    limit: 100,
    offset: 0,
    enabled: openAddModal
  })


  
  // const { data: lastImport, isLoading: isLastImportLoading } = useProductsLastImport()

  const { isHasAccess } = useCanAccess({ action: 'manage_certificate' })
  // VRG-455 — the same permission the /sales entry point checks.
  const { isHasAccess: canRecordSale } = useCanAccess({ action: 'record_sale' })

  // Server-side pagination is handled automatically by useProducts when params change


  const [{ bulkRemove }, { isLoading: isDeleting }] = useProductMutations({
    onSuccess: () => {
      // Clearing the selection lives HERE (delete succeeded), not in the modal's
      // close handler — cancelling the modal must keep the selection so the bulk-
      // action header above the table stays visible.
      setOpenBulkDeleteModal(false)
      setSelectedProducts([])
      setRowSelectionModel([])
      refetch()
    },
    onError: () => {
      // Error handling is done in the hook
    }
  })

  const [{ archive }] = useProductMutations({
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const [{ unarchive }] = useProductMutations({
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const handleArchiveProduct = useCallback((row: ProductWithCertificatesCount) => {
    archive({ id: row.id })
  }, [archive])

  const handleUnarchiveProduct = useCallback((row: ProductWithCertificatesCount) => {
    unarchive({ id: row.id })
  }, [unarchive])

  const handleViewProduct = useCallback((id: string) => {
    navigate(id, { state: referrer })
  }, [navigate, referrer])

  const handleEditProduct = useCallback((id: string) => {
    navigate(ROUTES.PRODUCT_EDIT(id), { state: referrer })
  }, [navigate, referrer])

  const handleDeleteProduct = useCallback((row: ProductWithCertificatesCount) => {
    setOpenDeleteModal(true)
    setSelectedProduct(row)
  }, [setOpenDeleteModal, setSelectedProduct])

  const handleCreateCertificates = useCallback((row: ProductWithCertificatesCount) => {
    setOpenCreateCertificatesModal(true)
    setSelectedProduct(row)
  }, [setOpenCreateCertificatesModal, setSelectedProduct])

  // VRG-455 — opens the shared assign-to-buyer modal seeded with this product,
  // so the brand never re-picks the product they are already looking at.
  const handleAssignToBuyer = useCallback((row: ProductWithCertificatesCount) => {
    setSelectedProduct(row)
    setOpenRecordSaleModal(true)
  }, [setSelectedProduct])

  const handleSyncProduct = useCallback((row: ProductWithCertificatesCount) => {
    const canSync = row.integration_provider === 'shopify' && Boolean(row.integration_id)

    if (!canSync) {
      return
    }

    setSyncingProductId(row.id)
    syncProduct(row.id)
  }, [setSyncingProductId, syncProduct])

  const handleBulkDelete = (selectedRows: ProductWithCertificatesCount[]) => {
    setSelectedProducts(selectedRows)
    setOpenBulkDeleteModal(true)
  }

  const handleCloseCreateCertificatesModal = () => {
    setOpenCreateCertificatesModal(false)
    setSelectedProduct(null)
  }

  const handleCloseBulkDeleteModal = () => {
    // Cancel/close only dismisses the modal — the selection (and thus the bulk-action
    // header) is preserved. The selection is cleared on delete SUCCESS (see the
    // useProductMutations onSuccess above).
    setOpenBulkDeleteModal(false)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setSelectedProduct(null)
  }

  // const handleDownload = () => {
  //   // exportDataAsExcel: (options?: GridExcelExportOptions) => Promise<void>
  //   // move this to table
  // }

  const handlePaginationModelChange = useCallback((newModel: { page: number; pageSize: number }) => {
    // Don't update pagination while data is loading to prevent rapid API calls
    if (isLoading) {
      return
    }

    // Prevent unnecessary updates if the model is the same
    if (paginationModel.page === newModel.page && paginationModel.pageSize === newModel.pageSize) {
      return
    }

    // Reset to page 0 when page size changes
    if (newModel.pageSize !== paginationModel.pageSize) {
      setPaginationModel({ page: 0, pageSize: newModel.pageSize })
    } else {
      setPaginationModel(newModel)
    }
  }, [paginationModel, isLoading])

  const getActionItems = useCallback(
    (row: ProductWithCertificatesCount) => {
      const canSyncProduct = row.integration_provider === 'shopify' && Boolean(row.integration_id)
      const isRowSyncing = canSyncProduct && syncingProductId === row.id && isSyncingProduct

      return [
        {
          // Primary product action (mirrors the detail-header primary). Gated by the
          // certificate permission — disabled + tooltip rather than hidden (§ access rule).
          label: 'Create certificates',
          onClick: () => handleCreateCertificates(row),
          disabled: !isHasAccess,
          tooltip: !isHasAccess ? "You need the 'Manage certificates' permission to create certificates." : undefined,
          icon: <BadgePlusIcon strokeWidth={1} />,
        },
        {
          // VRG-455 — the entry point where the INTENT actually forms. A brand
          // thinks "I sold this jacket to Jane" while looking at the jacket,
          // not while looking at a Sales list, so assigning a buyer has to be
          // reachable from the product. The /sales list still exists and still
          // earns its place: it is keyed on the BUYER, which is the axis this
          // page cannot index, and it is where void and bulk live.
          //
          // Permission DISABLES with the sentence naming what to ask for, never
          // hides (§27/§46) — an in-context action on a page already visible.
          label: 'Assign to buyer',
          onClick: () => handleAssignToBuyer(row),
          disabled: !canRecordSale,
          // Two DIFFERENT tooltips, because the row can be unavailable for one
          // reason and unclear for another. Lacking the permission, it names what
          // to ask for (§46). Holding it, it explains what the action DOES —
          // "Assign to buyer" does not convey that no certificate is picked and
          // that the buyer's own scan completes it, and a menu row is the last
          // place that can say so before the modal opens.
          tooltip: !canRecordSale
            ? "You need the 'Assign to buyer' permission. Ask an admin to grant it."
            : 'Record that you sold this product to someone, without picking a certificate. Their scan completes the transfer.',
          /*
            NOT `HandCoinsIcon`, which is the SIDEBAR's glyph for the Sales section
            and the SalesPage empty state. A section icon used as an action icon
            reads as "go to Sales", not "assign this product to a buyer" — the row
            looked like navigation sitting in a list of actions.

            `UserCheckIcon` because the action names a PERSON: it records who bought
            this, and their own scan completes the transfer. Unused elsewhere, so it
            carries no competing meaning, and deliberately not `UserPlusIcon`, which
            this app already spends on inviting a user (§21 — one icon per action,
            and never the same icon for two).
          */
          icon: <UserCheckIcon strokeWidth={1} />,
        },
        { label: 'Details', onClick: () => handleViewProduct(row.id), icon: <InfoIcon strokeWidth={1}/> },
        {
          label: isRowSyncing ? 'Syncing...' : 'Sync',
          onClick: () => handleSyncProduct(row),
          visible: canSyncProduct,
          // In-flight mutation, not permission or record state — stays
          // disabled+tooltip (never hidden), so the item doesn't vanish out
          // from under the cursor while a sync request is in progress.
          disabled: isRowSyncing,
          tooltip: isRowSyncing ? 'Sync in progress…' : undefined,
          icon: <RefreshCw strokeWidth={1} className={isRowSyncing ? 'animate-spin' : undefined} />,
        },
        { label: 'Edit', onClick: () => handleEditProduct(row.id), icon: <PencilIcon strokeWidth={1}/> },
        {
          label: 'Archive',
          onClick: () => handleArchiveProduct(row),
          visible: !row.archived,
          icon: <ArchiveIcon strokeWidth={1} />,
        },
        {
          label: 'Unarchive',
          onClick: () => handleUnarchiveProduct(row),
          visible: Boolean(row.archived),
          icon: <ArchiveRestoreIcon strokeWidth={1} />,
        },
        {
          // VRG-103 — a product with linked certificates can never be deleted
          // (only archived); hidden (record-state), not disabled, per §46 —
          // Archive above is always reachable, so hiding never strands the row.
          label: 'Delete',
          onClick: () => handleDeleteProduct(row),
          divider: true,
          destructive: true,
          visible: !row.certificates_count,
          icon: <TrashIcon strokeWidth={1} />,
        },
      ]
    },
    [
      handleCreateCertificates,
      isHasAccess,
      handleAssignToBuyer,
      canRecordSale,
      handleViewProduct,
      handleSyncProduct,
      handleEditProduct,
      handleArchiveProduct,
      handleUnarchiveProduct,
      handleDeleteProduct,
      isSyncingProduct,
      syncingProductId,
    ]
  )

  const getBulkActions = useCallback((selectedRows: ProductWithCertificatesCount[]) => [
    {
      label: 'Delete',
      onClick: () => handleBulkDelete(selectedRows),
      icon: <TrashIcon strokeWidth={1} />,
      destructive: true,
      visible: true
    }
  ], [])

  type Row = ProductWithCertificatesCount;

  const columns = useMemo<GridColDef<Row>[]>(
    () => [
      {
        field: 'title',
        headerName: 'Product',
        type: 'string',
        renderCell: (params: GridRenderCellParams<Row>) => {
          //   // const density = params.api.getRowHeight()
            const size = 48
          //   if (density == 'compact') {
          //     size = 60
          //   } else if (density == 'comfortable') {
          //     size = 32
          //   } else {
          //     size = 48
          //   }
             // TODO: get desnity change to work so can use to resize images on rows
            return (<ProductCell product={params.row} size={size} width={288}/>)
        },
        // 368px lines up this column's right border with the search box's right
        // edge above it (both measured from the shared 106px left inset every
        // list page shares) — the house default pinned-column width.
        width: 368,
        pinned: 'left',
        disableReorder: true,
      },
      {
        field: 'sku',
        headerName: 'SKU',
        type: 'string',
        width: 180,
        renderCell: (params: GridRenderCellParams<Row>) => (
          <span style={{ fontSize: '14px' }}>{params.value}</span>
        ),
      },
      {
        field: 'id',
        headerName: 'ID',
        type: 'string',
        width: 200,
        renderCell: ({ value }) => (<LinkCell link={value} route={ROUTES.PRODUCT(value)}/>)
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'string',
        width: 200,
        align: 'left',
        headerAlign: 'left',
        display: 'flex',
        // Display-only — Type is edited on the product Edit page (or, in future,
        // via bulk edit), never inline in the table. (VERA-455)
        renderCell: (params: GridRenderCellParams<Row>) => (
          params.value
            ? <CategoryCell category={params.value} filter />
            : <Typography variant="body2" color="text.secondary"></Typography>
        ),
      },
      {
        field: 'default_tag',
        headerName: 'Default Tag',
        type: 'string',
        sortable: false,
        filterable: false,
        // 200 clears label + chrome + glyph; unchanged. No sort arrow here either
        // (`sortable: false`), so the glyph takes the space the arrow would have.
        width: 200,
        renderHeader: headerWithTerm('Default Tag', 'tag'),
        align: 'left',
        headerAlign: 'left',
        display: 'flex',
        // Display-only — the default tag is set on the product Edit/detail page,
        // never inline in the table. (VERA-455)
        renderCell: (params: GridRenderCellParams<Row>) => (
          params.row.tag_shop_product
            ? <ProductPreview size="small" product={params.row.tag_shop_product as any} description={false} />
            : <Typography variant="body2" color="text.secondary"></Typography>
        ),
      },
      {
        // Renamed from "Quantity" — pairs it explicitly with "Uncertified
        // inventory" below (same noun, total vs. the uncertified subset of it)
        // instead of two columns that don't obviously relate by name alone.
        field: 'quantity',
        headerName: 'Inventory',
        type: 'number',
        // 140, up from 100, where the header truncated to "Inve…". A number column
        // needs room for the HEADER, not the values: "Inventory" is ~66px at 14px,
        // plus the sort icon (18) plus the title→icon gap plus the grid's cell
        // padding lands at ~116 minimum, and the widened gap made it worse.
        width: 140,
      },
      {
        // = quantity − certificates_count (see product.repository.ts) — of this
        // product's total inventory, how many units haven't had a certificate
        // created for them yet (still need to be tagged/encoded).
        field: 'untagged_inventory',
        headerName: 'Uncertified inventory',
        type: 'number',
        width: 200,
      },
      {
        field: 'certificates_count',
        headerName: 'Certificates',
        type: 'number',
        // 170, up from 120: "Certificates" is a long label, and with the sort arrow and
        // the term glyph it needs the room (§49 — size for the HEADER, not the values;
        // a count column looks like it needs almost none and is exactly where this bites).
        width: 170,
        align: 'center',
        headerAlign: 'center',
        // On the PRODUCTS table specifically, this is the disambiguation that earns its
        // place: the column counts certificates against one product, which is the
        // relationship the two words get confused over.
        renderHeader: headerWithTerm('Certificates', 'certificate'),
        // Aggregate-reference cell: the count links to this product's certificates.
        renderCell: (params: GridRenderCellParams<Row>) => (
          <AggregateCountCell
            backLabel="Products"
            count={params.row.certificates_count}
            // Seed the Certificates table's Filter model (single source of truth):
            // `?filters=field:operator:value` is parsed by Table into both the grid
            // filterModel AND the Filters side panel, so the prefilter shows up as a
            // real, removable Product filter (badge + drawer), not a bespoke chip.
            to={`${ROUTES.CERTIFICATES}?filters=product:isAnyOf:${params.row.id}`}
          />
        ),
      },
      {
        field: 'owners_count',
        headerName: 'Owners',
        type: 'number',
        // 140, up from 110 — label + sort arrow + term glyph.
        width: 140,
        align: 'center',
        headerAlign: 'center',
        renderHeader: headerWithTerm('Owners', 'owner'),
        // Aggregate-reference cell: the count links to the owners who hold a
        // certificate of this product (Products → Owners), seeding the Owners
        // table's filter model the same way the Certificates link does.
        renderCell: (params: GridRenderCellParams<Row>) => (
          <AggregateCountCell
            backLabel="Products"
            count={params.row.owners_count}
            to={`${ROUTES.OWNERS}?filters=product:isAnyOf:${params.row.id}`}
          />
        ),
      },
      {
        field: 'available',
        headerName: 'Availability',
        type: 'boolean',
        width: 150,
        // TODO: Tre, change availble field to availability in DB to be 'in_stock' and 'out_of_stock' to solve fitler trim issue
        renderCell: ({value}) => ( <StatusChipCell status={value ? 'in_stock' : 'not_in_stock'} statuses={PRODUCT_STOCK_STATUSES}/>),
      },
      {
        field: 'created_by_name',
        headerName: 'Created By',
        type: 'string',
        width: 200,
        sortable: false,
        // Shared UserCell (avatar + name), linked to the creator's user page
        // (/settings/users/:id) — like Owner→/owners. Avatar from the creator's
        // record (absolute URL); initials fallback; no link when there's no creator.
        renderCell: (params: GridRenderCellParams<Row>) => (
          <UserCell
            user={{ name: params.value, avatar: params.row.created_by_avatar }}
            route={params.row.created_by ? ROUTES.SETTINGS_COMPANY_USER(params.row.created_by) : undefined}
          />
        ),
      },
      {
        // Hidden by default (see `visibilityModel` on <Table>); surfaced via the
        // column picker. Pairs with the Filter-drawer "Show archived products".
        field: 'archived',
        headerName: 'Archived',
        type: 'boolean',
        width: 120,
        renderCell: ({ value }: GridRenderCellParams<Row>) => (
          value
            ? <StatusChipCell status="archived" statuses={PRODUCT_STATUSES} />
            : <Typography variant="body2" color="text.secondary"></Typography>
        ),
      },
      {
        field: 'actions',
        headerName: ' ',
        sortable: false,
        align: 'center',
        width: 54,
        pinned: 'right',
        renderCell: ({ row }) => <ActionDropdown items={getActionItems(row)} />,
      },
     ],[getActionItems, refetch]
  )        
  return (
    <>
      <Header
        title="Products"
        total={stableTotal} 
        rightComponents={
          <Stack direction="row" spacing={4} alignItems="center">
            <Button
              data-tour="products-create"
              onClick={() => setOpenAddModal(true)}
              startIcon={<PlusIcon strokeWidth={1}/>}
            >
              Add Products
            </Button>
          </Stack>
        }
      />
      {/*
        VRG-512: same sample-data caveat as Analytics (`IntroDataBanner`'s own docblock),
        surfaced here because seeded `intro` products are real rows in this table, not
        just in the aggregate figures. Self-suppresses to `null` for every tenant that
        doesn't hold intro data, so this costs nothing on a normal company's Products page.
        No `topOffset`/`fillBottom` on this page's `<Table>`, so its height is a fixed
        `vhMinus(176)` calc rather than a live measurement (§22) — the banner simply adds
        to the content above it and the page scrolls a little further, it does not fight
        the table for height the way it would on a `fillBottom` table.
      */}
      <IntroDataBanner />
      <Table
        dataTour="products-table"
        columns={columns}
        rows={products}
        loading={isLoading}
        bulkActions={getBulkActions}
        alwaysShowRowSelection={false}
        // ID and Created By are hidden by default — ID duplicates the pinned
        // Product cell's own subtitle, Created By is rarely the thing you're
        // scanning for. Both stay reachable via the Columns menu.
        visibilityModel={{ archived: false, id: false, created_by_name: false }}
        paginationMode="server"
        rowCount={stableTotal ?? -1}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        fetchExportPage={fetchExportPage}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        getRowClassName={(params: { row: ProductWithCertificatesCount }) =>
          params.row.archived ? 'row--archived' : ''
        }
        sx={{ '& .row--archived': { opacity: 0.55 } }}
        extraFilters={
          <ToggleField
            label="Show archived"
            checked={showArchivedDraft}
            onChange={setShowArchivedDraft}
          />
        }
        extraFiltersDirty={showArchivedDraft !== showArchived}
        onFiltersApply={() => setShowArchived(showArchivedDraft)}
        // Clear owns the extras too — reset the draft AND the applied value,
        // or the next commit bounces the toggle back on.
        onClearExtraFilters={() => {
          setShowArchivedDraft(false)
          setShowArchived(false)
        }}
        // filters={[
        //   { id: 1, field: 'quantity', operator: '>=', value: '80' },
        //   { id: 2, field: 'id', operator: 'isAnyOf', value: ['c4f3ae4e-0bb7-42d9-8fa8-42049d81cfba'] },
        // ]}
        emptyState={
          <EmptyState
          title="No products added"
          text="Import a list or create individual products using the actions above"
          icon={<ShoppingBagIcon strokeWidth={1} size={32}/>}
          />
        }
        viewKey="products"
      />
      <AddModal
        company={company}
        source={'products'}
        items={shopifyProductsData?.products || []}
        searchParams={{fields: ['title','sku'] as string[], preview: 'products'}}
        createRoute={ROUTES.PRODUCT_CREATE}
        open={openAddModal}
        onClose={() =>  setOpenAddModal(false)}
        refetch={refetch}
      />
      <DeleteProductModal
        product={selectedProduct}
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        refetch={refetch}
      />
      <CreateCertificatesModal
        product={selectedProduct}
        open={openCreateCertificatesModal}
        onClose={handleCloseCreateCertificatesModal}
        refetch={refetch}
      />
      {/* VRG-455 — the same modal /sales opens, seeded with the row's product
          so the brand never re-picks the thing they were already looking at. */}
      <RecordSaleModal
        open={openRecordSaleModal}
        onClose={() => setOpenRecordSaleModal(false)}
        refetch={refetch}
        canRecord={canRecordSale}
        seedProduct={selectedProduct}
      />
      <BulkDeleteProductsModal
        products={selectedProducts}
        open={openBulkDeleteModal}
        onClose={handleCloseBulkDeleteModal}
        onConfirm={() => {
          const productIds = selectedProducts.map(p => p.id)
          bulkRemove({ ids: productIds })
        }}
        isLoading={isDeleting}
      />
    </>
  )
}
